<template>
  <div class="page">
    <header class="header">
      <button class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </button>
      <div class="header-title">良不良移位</div>
      <div class="header-right"></div>
    </header>

    <main class="content" @click="closeAllDropdowns">
      <section class="panel">
        <div class="row">
          <div class="label">托盘码<span class="required">*</span></div>
          <div class="dropdown-wrap" @click.stop>
            <div class="input-with-scan">
              <input
                class="input"
                v-model="palletCode"
                placeholder="请输入/扫描托盘码"
                @focus="openFromPalletDropdown"
                @input="handleFromPalletInput"
              />
              <button
                v-if="palletCode"
                class="input-clear"
                type="button"
                @click="clearPalletCode"
                aria-label="清除"
              >
                ×
              </button>
              <button
                @click="startFromPalletScan"
                class="scan-btn"
                aria-label="扫码"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  width="20"
                  height="20"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <path
                    d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"
                  />
                </svg>
              </button>
            </div>

            <div v-if="fromPalletDropdownOpen" class="dropdown">
              <div v-if="fromPalletLoading" class="status">加载中...</div>
              <div v-else-if="fromPalletError" class="status">
                {{ fromPalletError }}
              </div>
              <div
                v-else-if="palletCode && fromPalletOptions.length === 0"
                class="status"
              >
                暂无数据
              </div>
              <div v-else-if="fromPalletOptions.length" class="pallet-options">
                <button
                  v-for="p in fromPalletOptions"
                  :key="p.id"
                  type="button"
                  class="pallet-option"
                  @click="confirmFromPallet(p)"
                >
                  <div class="pallet-option-head">
                    <div class="pallet-option-main">
                      {{ p.palletCode || "-" }}
                    </div>
                  </div>
                  <div class="pallet-option-sub">
                    {{ p.storeName ? `${p.storeName}` : "" }}
                    {{ p.wareAreaName ? ` | ${p.wareAreaName}` : "" }}
                    {{ p.wareName ? ` | ${p.wareName}` : "" }}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 供应商搜索 -->
        <div class="row">
          <div class="label">供应商</div>
          <div class="dropdown-wrap" @click.stop>
            <input
              class="input"
              v-model="supplierKeyword"
              placeholder="请输入供应商名称"
              @focus="openSupplierDropdown"
              @input="handleSupplierInput"
            />
            <button
              v-if="supplierKeyword"
              class="input-clear"
              type="button"
              @click="clearSupplier"
              aria-label="清除"
            >
              ×
            </button>

            <div v-if="supplierDropdownOpen" class="dropdown">
              <div v-if="supplierLoading" class="status">加载中...</div>
              <div v-else-if="supplierError" class="status">
                {{ supplierError }}
              </div>
              <div
                v-else-if="supplierKeyword && supplierOptions.length === 0"
                class="status"
              >
                暂无数据
              </div>
              <div v-else-if="supplierOptions.length" class="pallet-options">
                <button
                  v-for="s in supplierOptions"
                  :key="s.id"
                  type="button"
                  class="pallet-option"
                  @click="confirmSupplier(s)"
                >
                  <div class="pallet-option-head">
                    <div class="pallet-option-main">
                      {{ s.supplierName || "-" }}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 物料编码搜索 -->
        <div class="row">
          <div class="label">物料编码</div>
          <div class="dropdown-wrap" @click.stop>
            <input
              class="input"
              v-model="materialCodeKeyword"
              placeholder="请输入物料编码"
              @focus="openMaterialCodeDropdown"
              @input="handleMaterialCodeInput"
            />
            <button
              v-if="materialCodeKeyword"
              class="input-clear"
              type="button"
              @click="clearMaterialCode"
              aria-label="清除"
            >
              ×
            </button>

            <div v-if="materialCodeDropdownOpen" class="dropdown">
              <div v-if="materialCodeLoading" class="status">加载中...</div>
              <div v-else-if="materialCodeError" class="status">
                {{ materialCodeError }}
              </div>
              <div
                v-else-if="
                  materialCodeKeyword && materialCodeOptions.length === 0
                "
                class="status"
              >
                暂无数据
              </div>
              <div
                v-else-if="materialCodeOptions.length"
                class="pallet-options"
              >
                <button
                  v-for="m in materialCodeOptions"
                  :key="m.id"
                  type="button"
                  class="pallet-option"
                  @click="confirmMaterialCode(m)"
                >
                  <div class="pallet-option-head">
                    <div class="pallet-option-main">
                      {{ m.materialNo || "-" }}
                    </div>
                  </div>
                  <div class="pallet-option-sub">
                    {{ m.supplierName || "" }}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <button
          class="primary-btn"
          type="button"
          :disabled="loading"
          @click="handleSearch"
        >
          {{ loading ? "搜索中..." : "搜索" }}
        </button>
      </section>

      <section class="panel" v-if="searched">
        <div class="panel-title">库存详情</div>
        <div v-if="loading" class="status">加载中...</div>
        <div v-else-if="errorMsg" class="status">{{ errorMsg }}</div>
        <div v-else-if="materialGroups.length === 0" class="status">
          暂无数据
        </div>

        <div v-else class="material-groups">
          <section
            v-for="g in materialGroups"
            :key="g.materialId || g.materialNo"
            class="material-group"
          >
            <div class="material-group-head">
              <div class="material-left">
                <div class="material-title-line">
                  <span v-if="g.supplierName" class="supplier">{{
                    g.supplierName
                  }}</span>
                  <span class="material-no">{{ g.materialNo || "-" }}</span>
                </div>
              </div>
              <div class="material-right">
                <div class="qty">
                  <div class="k">合计</div>
                  <div class="v">{{ formatQty(g.totalStockNum) }}</div>
                </div>
              </div>
            </div>

            <div class="batch-list">
              <button
                v-for="b in g.batches"
                :key="b.key"
                type="button"
                class="batch-item"
                :class="{ selected: selectedRowKey === rowKeyOf(b) }"
                :disabled="preMoveLoading"
                @click="selectMaterial(b)"
              >
                <div class="material-left">
                  <div class="batch-main">
                    <span>{{
                      b.batchNo ? `批次：${formatBatchNo(b.batchNo)}` : "-"
                    }}</span>
                    <span
                      class="badge"
                      :class="b.isBad === 1 ? 'badge-bad' : 'badge-good'"
                    >
                      {{ b.isBad === 1 ? "不良" : "良品" }}
                    </span>
                  </div>
                  <div class="batch-sub">库存：{{ formatQty(b.stockNum) }}</div>
                </div>
                <div class="material-right">
                  <input
                    class="material-check"
                    type="radio"
                    :checked="selectedRowKey === rowKeyOf(b)"
                    readonly
                  />
                </div>
              </button>
            </div>
          </section>
        </div>
      </section>

      <!-- 分页控件 -->
      <div
        v-if="
          palletDetail &&
          (palletDetail.current || palletDetail.size) &&
          !loading
        "
        class="pagination"
      >
        <button
          class="pagination-btn"
          :disabled="palletDetail.current <= 1"
          @click="handlePrevPage"
        >
          上一页
        </button>
        <div class="pagination-info">第 {{ palletDetail.current || 1 }} 页</div>
        <button
          class="pagination-btn"
          :disabled="!hasNextPage"
          @click="handleNextPage"
        >
          下一页
        </button>
      </div>

      <div v-if="showConfirmModal" class="modal">
        <div class="modal-overlay" @click="closeConfirmModal"></div>
        <div class="modal-sheet">
          <div class="modal-head">
            <div class="modal-title">确认转换</div>
            <button
              type="button"
              class="modal-close"
              @click="closeConfirmModal"
            >
              ×
            </button>
          </div>

          <div class="modal-body">
            <div class="modal-summary">
              <div class="sum-main">{{ selectedRow?.materialNo || "-" }}</div>
              <div class="sum-sub">
                {{
                  selectedRow?.batchNo
                    ? `批次：${formatBatchNo(selectedRow.batchNo)}`
                    : "-"
                }}
                {{ selectedRow?.isBad === 1 ? " | 不良" : " | 良品" }}
                {{
                  selectedRow?.stockNum
                    ? ` | 库存：${formatQty(selectedRow.stockNum)}`
                    : ""
                }}
              </div>
            </div>

            <div class="mode-switch">
              <button
                class="mode-btn"
                :class="{ active: locationMode === 'scan' }"
                @click="locationMode = 'scan'"
              >
                扫码库位
              </button>
              <button
                class="mode-btn"
                :class="{ active: locationMode === 'select' }"
                @click="locationMode = 'select'"
              >
                选择库位
              </button>
            </div>

            <div class="row">
              <div class="label">新托盘码</div>
              <div class="dropdown-wrap">
                <div class="input-with-scan">
                  <input
                    v-model="palletKeyword"
                    class="input"
                    placeholder="请输入新托盘码"
                    @focus="openPalletDropdown"
                    @input="handlePalletInput"
                  />
                  <button
                    v-if="palletKeyword"
                    class="input-clear"
                    type="button"
                    @click="clearPalletKeyword"
                    aria-label="清除"
                  >
                    ×
                  </button>
                  <button
                    @click="startPalletScan"
                    class="scan-btn"
                    aria-label="扫码"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      width="20"
                      height="20"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <path
                        d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"
                      />
                    </svg>
                  </button>
                </div>

                <div v-if="palletDropdownOpen" class="dropdown">
                  <div v-if="palletLoading" class="status">加载中...</div>
                  <div v-else-if="palletError" class="status">
                    {{ palletError }}
                  </div>
                  <div
                    v-else-if="palletKeyword && palletOptions.length === 0"
                    class="status"
                  >
                    暂无数据
                  </div>
                  <div v-else-if="palletOptions.length" class="pallet-options">
                    <button
                      v-for="p in palletOptions"
                      :key="p.id"
                      type="button"
                      class="pallet-option"
                      :class="{ active: selectedNewPalletId === String(p.id) }"
                      @click="confirmNewPallet(p)"
                    >
                      <div class="pallet-option-head">
                        <div class="pallet-option-main">
                          {{ p.palletCode || "-" }}
                        </div>
                      </div>
                      <div class="pallet-option-sub">
                        {{ p.storeName ? `${p.storeName}` : "" }}
                        {{ p.wareAreaName ? ` | ${p.wareAreaName}` : "" }}
                        {{ p.wareName ? ` | ${p.wareName}` : "" }}
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <template v-if="locationMode === 'select'">
              <button class="row-btn" type="button" @click="openWareSelector">
                <div class="label">新库位</div>
                <div class="value" :class="{ placeholder: !newWareText }">
                  {{ newWareText || "请选择新库位" }}
                </div>
                <div class="arrow">›</div>
              </button>
            </template>

            <template v-else>
              <div class="row">
                <div class="label">新库位</div>
                <div class="dropdown-wrap">
                  <input
                    v-model="scanWareNo"
                    class="input"
                    :disabled="scanWareLoading || isHighRackPallet"
                    :placeholder="
                      isHighRackPallet
                        ? '高架仓库位已自动绑定'
                        : '请扫描/输入库位码'
                    "
                    @input="handleWareScan"
                  />
                  <div v-if="scanWareLoading" class="dropdown-loading">
                    加载中...
                  </div>
                </div>
              </div>
              <div v-if="scanWareError" class="form-error">
                {{ scanWareError }}
              </div>
            </template>

            <div class="row">
              <div class="label">类型</div>
              <div class="seg">
                <button
                  type="button"
                  class="seg-btn"
                  :class="{ active: direction === 0 }"
                  :disabled="selectedRow?.isBad === 1"
                  @click="direction = 0"
                >
                  良 → 不
                </button>
                <button
                  type="button"
                  class="seg-btn"
                  :class="{ active: direction === 1 }"
                  :disabled="selectedRow?.isBad === 0"
                  @click="direction = 1"
                >
                  不 → 良
                </button>
              </div>
            </div>

            <div class="row">
              <div class="label">数量</div>
              <input
                class="input"
                inputmode="decimal"
                v-model="moveQty"
                placeholder="请输入移位数量"
              />
            </div>

            <div class="row">
              <div class="label">备注</div>
              <input
                class="input"
                v-model="remark"
                placeholder="请输入备注"
              />
            </div>
          </div>

          <div class="modal-foot">
            <button
              class="action-btn"
              type="button"
              :disabled="submitLoading"
              @click="submit"
            >
              {{ submitLoading ? "提交中..." : "确认转换" }}
            </button>
            <div class="form-tip">一次只能选择一个批次。</div>
          </div>
        </div>
      </div>

      <div v-if="showWareSelectModal" class="modal">
        <div class="modal-overlay" @click="closeWareSelectModal"></div>
        <div class="modal-sheet">
          <div class="modal-head">
            <div class="modal-title">选择库位</div>
            <button
              type="button"
              class="modal-close"
              @click="closeWareSelectModal"
            >
              ×
            </button>
          </div>
          <div class="modal-body">
            <div class="ware-list">
              <button
                v-for="ware in scanWareResults"
                :key="ware.id"
                class="ware-item"
                @click="selectScannedWare(ware)"
              >
                <div class="ware-name">{{ ware.wareName || ware.wareNo }}</div>
                <div class="ware-area">{{ ware.wareAreaName }}</div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showWareSelector" class="selector">
        <div class="selector-overlay" @click="closeWareSelector"></div>
        <div class="selector-panel">
          <header class="selector-header">
            <button
              class="selector-back"
              @click="closeWareSelector"
              aria-label="返回"
            >
              ×
            </button>
            <div class="selector-title">库位搜索</div>
            <div class="selector-right"></div>
          </header>

          <div class="selector-search">
            <input
              type="text"
              v-model="locationSearchKeyword"
              placeholder="搜索库位"
              class="search-input"
              @input="handleLocationSearch"
            />
          </div>

          <div class="selector-warehouse-bar">
            <div v-if="warehouseLoading" class="selector-warehouse-loading">
              加载仓库...
            </div>
            <div v-else class="warehouse-tabs">
              <button
                v-for="w in warehouseList"
                :key="w.id"
                type="button"
                class="warehouse-tab"
                :class="{ active: selectedWarehouseId === w.id }"
                @click="selectWarehouse(w)"
              >
                {{ w.wareName || "-" }}
              </button>
            </div>
          </div>

          <div v-if="wareSelectorLoading" class="selector-loading">
            加载中...
          </div>
          <div v-else-if="wareSelectorError" class="selector-error">
            {{ wareSelectorError }}
          </div>

          <div v-else class="selector-body">
            <aside class="areas">
              <button
                v-for="a in wareAreas"
                :key="a.id"
                type="button"
                class="area-item"
                :class="{ active: activeAreaId === a.id }"
                @click="selectArea(a)"
              >
                {{ a.wareName || a.wareNo || "-" }}
              </button>
            </aside>

            <section class="locations">
              <div class="locations-title">{{ activeAreaName }}</div>
              <div class="location-grid">
                <button
                  v-for="l in activeLocations"
                  :key="l.id"
                  type="button"
                  class="location-card"
                  :class="{ selected: selectedLocationId === String(l.id) }"
                  @click="selectLocation(l)"
                >
                  <div class="location-name">
                    {{ l.wareName || l.wareNo || "-" }}
                  </div>
                  <div class="location-sub">
                    剩余托盘位：{{ l.remainPallet ?? "-" }}
                  </div>
                </button>
              </div>
            </section>
          </div>

          <div class="selector-footer">
            <button
              class="selector-confirm"
              type="button"
              :disabled="!selectedLocationId"
              @click="confirmWareSelector"
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- 托盘码扫码组件 -->
    <CameraScan
      v-model:visible="fromPalletScanVisible"
      title="扫描托盘码"
      @success="handleFromPalletScanSuccess"
      @close="fromPalletScanVisible = false"
    />

    <!-- 新托盘码扫码组件 -->
    <CameraScan
      v-model:visible="palletScanVisible"
      title="扫描新托盘码"
      @success="handlePalletScanSuccess"
      @close="palletScanVisible = false"
    />

  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { ElMessageBox } from "element-plus";
import toast from "@/utils/toast";
import {
  getPalletCodeList,
  getPalletDetail,
  getStockWareList,
  getWareListByParentId,
  getWarePage,
  searchMaterialByNo,
  getSupplierList,
} from "@/api/stockTransfer";
import { qualityTransferMove } from "@/api/stockCheck";
import CameraScan from "@/components/CameraScan";

export default {
  name: "QualityTransfer",
  components: {
    CameraScan,
  },
  setup() {
    const router = useRouter();

    onMounted(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

    const handleBack = () => router.back();

    const palletCode = ref("");
    const fromPalletScanVisible = ref(false);

    const fromPalletDropdownOpen = ref(false);
    const fromPalletLoading = ref(false);
    const fromPalletError = ref("");
    const fromPalletOptions = ref([]);
    let fromPalletSearchTimer = null;

    // 物料编码搜索相关
    const materialCodeKeyword = ref("");
    const materialCodeDropdownOpen = ref(false);
    const materialCodeLoading = ref(false);
    const materialCodeError = ref("");
    const materialCodeOptions = ref([]);
    const materialCodeSearchSeq = ref(0);
    const selectedMaterialId = ref("");

    // 供应商搜索相关
    const supplierKeyword = ref("");
    const supplierDropdownOpen = ref(false);
    const supplierLoading = ref(false);
    const supplierError = ref("");
    const supplierOptions = ref([]);
    const selectedSupplier = ref(null);
    const selectedSupplierName = ref("");
    const supplierSearchSeq = ref(0);

    const palletDetail = ref(null);

    // 分页相关变量
    const currentPage = ref(1);
    const pageSize = ref(20);
    const hasNextPage = computed(() => {
      const d = palletDetail.value || {};
      return d.current < d.pages;
    });

    const loading = ref(false);
    const preMoveLoading = ref(false);
    const errorMsg = ref("");
    const searched = ref(false);

    const materialGroups = ref([]);

    const selectedRow = ref(null);
    const selectedRowKey = computed(() => rowKeyOf(selectedRow.value));

    const showConfirmModal = ref(false);

    const direction = ref(0);
    const moveQty = ref("");
    const remark = ref("");
    const submitLoading = ref(false);

    const rowKeyOf = (m) => {
      if (!m) return "";
      const materialId = String(m.materialId || "");
      const batchNo = String(m.batchNo || "").trim() || "-";
      const isBad = String(m?.isBad || 0);
      return `${materialId}_${batchNo}_${isBad}`;
    };

    const openMaterialCodeDropdown = () => {
      materialCodeDropdownOpen.value = true;
      materialCodeError.value = "";
      if ((materialCodeKeyword.value || "").trim()) {
        handleMaterialCodeInput();
      }
    };

    const closeMaterialCodeDropdown = () => {
      materialCodeDropdownOpen.value = false;
    };

    const closeAllDropdowns = () => {
      materialCodeDropdownOpen.value = false;
      supplierDropdownOpen.value = false;
    };

    const handleSupplierInput = async () => {
      const keyword = (supplierKeyword.value || "").trim();
      supplierError.value = "";

      const seq = supplierSearchSeq.value + 1;
      supplierSearchSeq.value = seq;

      if (!keyword) {
        supplierOptions.value = [];
        return;
      }

      supplierLoading.value = true;
      try {
        const res = await getSupplierList({ supplierName: keyword });
        if (seq !== supplierSearchSeq.value) return;
        const data = res?.data || {};
        const records = Array.isArray(data?.records) ? data.records : [];
        supplierOptions.value = records;
        if (records.length === 1) {
          confirmSupplier(records[0]);
        }
      } catch (e) {
        if (seq !== supplierSearchSeq.value) return;
        supplierError.value = e?.message || "搜索失败";
      } finally {
        if (seq === supplierSearchSeq.value) {
          supplierLoading.value = false;
        }
      }
    };

    const openSupplierDropdown = () => {
      supplierDropdownOpen.value = true;
      supplierError.value = "";
      if ((supplierKeyword.value || "").trim()) {
        handleSupplierInput();
      }
    };

    const clearSupplier = () => {
      supplierKeyword.value = "";
      supplierOptions.value = [];
      selectedSupplier.value = null;
      selectedSupplierName.value = "";
      supplierDropdownOpen.value = false;
    };

    const confirmSupplier = (s) => {
      selectedSupplier.value = s || null;
      selectedSupplierName.value = s?.supplierName || "";
      supplierKeyword.value = s?.supplierName || "";
      supplierDropdownOpen.value = false;
    };

    const closeSupplierDropdown = () => {
      supplierDropdownOpen.value = false;
    };

    const handleMaterialCodeInput = async () => {
      const keyword = (materialCodeKeyword.value || "").trim();
      materialCodeError.value = "";
      const seq = materialCodeSearchSeq.value + 1;
      materialCodeSearchSeq.value = seq;

      if (!keyword) {
        materialCodeOptions.value = [];
        selectedMaterialId.value = "";
        return;
      }

      materialCodeLoading.value = true;
      try {
        const searchParams = { materialNo: keyword };
        if (selectedSupplier.value?.id) {
          searchParams.supplierId = selectedSupplier.value.id;
        }
        const res = await searchMaterialByNo(searchParams);
        if (seq !== materialCodeSearchSeq.value) return;
        const data = res?.data || {};
        const records = Array.isArray(data?.records) ? data.records : [];
        materialCodeOptions.value = records;
        // 如果只有一个匹配结果，自动选择
        if (records.length === 1) {
          confirmMaterialCode(records[0]);
        }
      } catch (e) {
        if (seq !== materialCodeSearchSeq.value) return;
        materialCodeError.value = e?.message || "搜索失败";
      } finally {
        if (seq === materialCodeSearchSeq.value) {
          materialCodeLoading.value = false;
        }
      }
    };

    const confirmMaterialCode = (m) => {
      if (m) {
        selectedMaterialId.value = m.id;
        materialCodeKeyword.value = m.materialNo || "";
      } else {
        selectedMaterialId.value = "";
        materialCodeKeyword.value = "";
      }
      materialCodeDropdownOpen.value = false;
    };

    const clearMaterialCode = () => {
      selectedMaterialId.value = "";
      materialCodeKeyword.value = "";
      materialCodeOptions.value = [];
    };

    const clearPalletCode = () => {
      palletCode.value = "";
      selectedRow.value = null;
      palletDetail.value = null;
      fromPalletOptions.value = [];
      fromPalletDropdownOpen.value = false;
    };

    const clearPalletKeyword = () => {
      palletKeyword.value = "";
      selectedNewPallet.value = null;
      palletOptions.value = [];
      palletDropdownOpen.value = false;
    };

    const handleFromPalletSearch = async () => {
      const keyword = String(palletCode.value || "").trim();
      fromPalletError.value = "";
      fromPalletOptions.value = [];
      if (!keyword) return;

      fromPalletLoading.value = true;
      try {
        const res = await getPalletCodeList({ palletCode: keyword });
        const data = res?.data;
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];

        if (list.length === 1) {
          // 如果只有一条记录，直接查询
          palletCode.value = String(list[0]?.palletCode || "");
          fromPalletDropdownOpen.value = false;
          handleSearch();
        } else {
          // 如果大于1条记录，显示下拉选择
          fromPalletOptions.value = list;
        }
      } catch (e) {
        fromPalletError.value = e?.message || "搜索失败";
      } finally {
        fromPalletLoading.value = false;
      }
    };

    const openFromPalletDropdown = () => {
      fromPalletDropdownOpen.value = true;
      fromPalletError.value = "";
      fromPalletOptions.value = [];
      if (String(palletCode.value || "").trim()) {
        handleFromPalletSearch();
      }
    };

    const startFromPalletScan = () => {
      fromPalletScanVisible.value = true;
    };

    const handleFromPalletScanSuccess = (decodedText) => {
      const text = String(decodedText || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[\r\n\t]/g, "")
        .trim();

      if (!text) return;

      palletCode.value = text;
      fromPalletScanVisible.value = false;
      fromPalletDropdownOpen.value = true;
      handleFromPalletInput();
    };

    const handleFromPalletInput = () => {
      fromPalletDropdownOpen.value = true;
      if (fromPalletSearchTimer) clearTimeout(fromPalletSearchTimer);
      fromPalletSearchTimer = setTimeout(() => {
        handleFromPalletSearch();
      }, 300);
    };

    const confirmFromPallet = (p) => {
      palletCode.value = String(p?.palletCode || "");
      fromPalletDropdownOpen.value = false;
      handleSearch();
    };

    const selectMaterial = (m) => {
      if (preMoveLoading.value) return;
      const row = m || null;
      if (!row) return;

      const code = String(palletCode.value || "").trim();
      if (!code) {
        toast.error("请输入托盘码");
        return;
      }

      const key = row.key || rowKeyOf(row);
      let found = null;
      for (const g of materialGroups.value) {
        const batches = Array.isArray(g?.batches) ? g.batches : [];
        found = batches.find((b) => b.key === key);
        if (found) break;
      }

      if (!found) {
        selectedRow.value = null;
        toast.error("批次信息已变更，请重新选择");
        return;
      }

      selectedRow.value = found;
      direction.value = found?.isBad === 1 ? 1 : 0;

      scannedWareLocation.value = null;
      scanWareNo.value = "";
      scanWareResults.value = [];

      const palletData = palletDetail.value || {};
      if (Number(palletData.palletType) === 1 && palletData.wareId) {
        locationMode.value = "scan";
        scannedWareLocation.value = {
          id: palletData.wareId,
          wareName: palletData.wareName,
          wareNo: palletData.wareNo,
          wareAreaName: palletData.wareAreaName,
        };
        scanWareNo.value = palletData.wareName || palletData.wareNo || "";
        newWareId.value = String(palletData.wareId);
      }

      showConfirmModal.value = true;
    };

    const closeConfirmModal = () => {
      showConfirmModal.value = false;
    };

    const resetConfirmForm = () => {
      newWareId.value = "";
      newWareText.value = "";
      selectedNewPallet.value = null;
      palletKeyword.value = "";
      palletOptions.value = [];
      palletDropdownOpen.value = false;
      palletError.value = "";
      moveQty.value = "";
      remark.value = "";
      direction.value = 0;
      scannedWareLocation.value = null;
      scanWareNo.value = "";
      scanWareError.value = "";
    };

    const formatQty = (val) => {
      if (val === null || val === undefined || val === "") return "-";
      const n = Number(val);
      if (Number.isNaN(n)) return String(val);
      return n.toFixed(3);
    };

    const formatBatchNo = (batchNo, maxLength = 12) => {
      if (!batchNo) return "-";
      const str = String(batchNo).trim();
      if (str.length <= maxLength) return str;
      return str.substring(0, maxLength) + "...";
    };

    const handleSearch = async (resetPage = true) => {
      const code = String(palletCode.value || "").trim();
      if (!code) {
        toast.error("请输入托盘码");
        return;
      }

      if (resetPage) {
        currentPage.value = 1;
      }

      searched.value = true;
      loading.value = true;
      errorMsg.value = "";
      selectedRow.value = null;
      materialGroups.value = [];

      try {
        const params = {
          palletCode: code,
          current: currentPage.value,
          size: pageSize.value,
        };
        if (selectedMaterialId.value) {
          params.materialAndId = selectedMaterialId.value;
        } else if (
          materialCodeKeyword.value &&
          materialCodeOptions.value.length > 0
        ) {
          const firstMaterial = materialCodeOptions.value[0];
          if (firstMaterial && firstMaterial.id) {
            params.materialAndId = firstMaterial.id;
            selectedMaterialId.value = firstMaterial.id;
          }
        }
        if (selectedSupplier.value?.id) {
          params.supplierId = selectedSupplier.value.id;
        }
        const res = await getPalletDetail(params);

        // 适配新的数据结构
        const data = res?.data || {};
        const records = Array.isArray(data.records) ? data.records : [];

        if (records.length > 0) {
          // 合并所有托盘的物料数据
          let allMaterials = [];
          let palletInfo = records[0]; // 使用第一个托盘的信息

          records.forEach((record) => {
            if (Array.isArray(record.materialList)) {
              allMaterials = allMaterials.concat(record.materialList);
            }
          });

          // 构造兼容的detail结构
          const d = {
            ...palletInfo,
            materialList: allMaterials,
            current: data.current,
            size: data.size,
            total: data.total,
            pages: data.pages,
          };

          palletDetail.value = d;
          materialGroups.value = buildGroupsFromDetail(d);
        } else {
          const d = {
            ...data,
            materialList: [],
            current: data.current,
            size: data.size,
            total: data.total,
            pages: data.pages,
          };

          palletDetail.value = d;
          materialGroups.value = buildGroupsFromDetail(d);
        }
      } catch (e) {
        errorMsg.value = e?.message || "查询失败";
      } finally {
        loading.value = false;
      }
    };

    const handlePrevPage = async () => {
      if (currentPage.value <= 1) return;
      currentPage.value -= 1;
      await handleSearch(false);
    };

    const handleNextPage = async () => {
      if (!hasNextPage.value) return;
      currentPage.value += 1;
      await handleSearch(false);
    };

    const buildGroupsFromDetail = (d) => {
      const materials = Array.isArray(d?.materialList) ? d.materialList : [];
      const groups = [];

      for (const m of materials) {
        const materialId = String(m?.materialId || m?.id || "");
        const materialNo = m?.materialNo;
        const materialDesc = m?.materialDesc;
        const supplierName = m?.supplierName;

        const stockRows =
          Array.isArray(m?.stockList) && m.stockList.length ? m.stockList : [];
        const batches = [];

        if (stockRows.length) {
          for (const s of stockRows) {
            const batchNo = String(s?.batchNo || "").trim() || "-";
            const isBad = String(s?.isBad || 0);
            batches.push({
              key: `${materialId}_${batchNo}_${isBad}`,
              materialId,
              materialNo,
              materialDesc,
              batchNo,
              stockNum: s?.stockNum ?? null,
              storeId: s?.storeId || m?.storeId || d?.storeId,
              wareId: s?.wareId || m?.wareId || d?.wareId,
              palletId: s?.palletId || m?.palletId || d?.palletId || d?.id,
              isBad: s?.isBad,
            });
          }
        } else {
          const batchNo = String(m?.batchNo || "").trim() || "-";
          const isBad = String(m?.isBad || 0);
          batches.push({
            key: `${materialId}_${batchNo}_${isBad}`,
            materialId,
            materialNo,
            materialDesc,
            batchNo,
            stockNum: m?.stockNum ?? null,
            storeId: m?.storeId || d?.storeId,
            wareId: m?.wareId || d?.wareId,
            palletId: m?.palletId || d?.palletId || d?.id,
            isBad: m?.isBad,
          });
        }

        const totalStockNum = batches.reduce(
          (sum, b) => sum + (Number(b.stockNum) || 0),
          0,
        );

        groups.push({
          materialId,
          materialNo,
          materialDesc,
          supplierName,
          totalStockNum,
          batches,
        });
      }

      return groups;
    };

    const asId = (val) => {
      const s = String(val ?? "").trim();
      if (!s) return "";
      if (!/^\d+$/.test(s)) return "";
      return s;
    };

    const showWareSelector = ref(false);
    const wareSelectorLoading = ref(false);
    const wareSelectorError = ref("");
    const warehouseLoading = ref(false);
    const warehouseList = ref([]);
    const selectedWarehouseId = ref("");
    const warehouseName = ref("");
    const wareAreas = ref([]);
    const wareParentId = ref("");
    const activeAreaId = ref("");
    const activeAreaName = ref("");
    const selectedArea = ref(null);
    const selectedLocation = ref(null);
    const selectedLocationId = computed(() =>
      String(selectedLocation.value?.id || ""),
    );
    const locationSearchKeyword = ref("");

    const newWareId = ref("");
    const newWareText = ref("");
    const newWareTextComputed = computed(() =>
      String(newWareText.value || "").trim(),
    );

    // 库位选择模式: 'select' 选择模式, 'scan' 扫码模式
    const locationMode = ref("scan");
    const scanWareNo = ref("");
    const scanWareLoading = ref(false);
    const scanWareError = ref("");
    const scanWareResults = ref([]);
    const showWareSelectModal = ref(false);
    const scannedWareLocation = ref(null);

    // 判断是否为高架仓托盘 (palletType === 1)
    const isHighRackPallet = computed(() => {
      const d = palletDetail.value || {};
      return Number(d.palletType) === 1;
    });

    const activeLocations = computed(() => {
      const area = wareAreas.value.find(
        (a) => String(a.id) === String(activeAreaId.value),
      );
      const locations = Array.isArray(area?.wareList) ? area.wareList : [];

      if (!locationSearchKeyword.value.trim()) {
        return locations;
      }

      const keyword = locationSearchKeyword.value.toLowerCase();
      return locations.filter((loc) => {
        const name = (loc.wareName || loc.wareNo || "").toLowerCase();
        return name.includes(keyword);
      });
    });

    const handleLocationSearch = () => {
      // 搜索逻辑已在activeLocations计算属性中处理
    };

    const loadWarehouses = async () => {
      warehouseLoading.value = true;
      warehouseList.value = [];
      selectedWarehouseId.value = "";
      try {
        const res = await getStockWareList();
        const data = res?.data;
        warehouseList.value = Array.isArray(data) ? data : [];
      } catch (e) {
        wareSelectorError.value = e?.message || "加载仓库失败";
      } finally {
        warehouseLoading.value = false;
      }
    };

    const selectWarehouse = async (warehouse) => {
      if (!warehouse?.id) return;
      selectedWarehouseId.value = warehouse.id;
      warehouseName.value = warehouse?.wareName || "";
      wareAreas.value = [];
      activeAreaId.value = "";
      activeAreaName.value = "";
      selectedArea.value = null;
      selectedLocation.value = null;
      wareSelectorError.value = "";

      wareSelectorLoading.value = true;
      try {
        const res = await getWareListByParentId({ id: warehouse.id });
        const data = res?.data;
        const nextAreas = Array.isArray(data?.wareList) ? data.wareList : [];
        wareAreas.value = nextAreas;
        const first = nextAreas[0];
        activeAreaId.value = first?.id || "";
        activeAreaName.value = first?.wareName || first?.wareNo || "";
      } catch (e) {
        wareSelectorError.value = e?.message || "加载失败";
      } finally {
        wareSelectorLoading.value = false;
      }
    };

    const openWareSelector = async () => {
      showWareSelector.value = true;
      wareSelectorError.value = "";

      if (!warehouseList.value.length) {
        await loadWarehouses();
      }

      if (!selectedWarehouseId.value && warehouseList.value.length) {
        await selectWarehouse(warehouseList.value[0]);
      }
    };

    const closeWareSelector = () => {
      showWareSelector.value = false;
    };

    const selectArea = (area) => {
      activeAreaId.value = area?.id || "";
      activeAreaName.value = area?.wareName || area?.wareNo || "";
    };

    const selectAllLocations = () => {
      selectedLocation.value = { id: "__all__", wareName: "全部库位" };
    };

    const selectLocation = (loc) => {
      const area =
        wareAreas.value.find(
          (a) => String(a.id) === String(activeAreaId.value),
        ) || null;
      selectedArea.value = area;
      selectedLocation.value = loc;
    };

    const confirmWareSelector = () => {
      if (!selectedLocationId.value) return;
      newWareId.value = String(selectedLocation.value?.id || "");
      const areaName = selectedArea.value?.wareName;
      const locName = selectedLocation.value?.wareName;
      newWareText.value =
        areaName && locName
          ? `${areaName} / ${locName}`
          : String(locName || areaName || "");
      closeWareSelector();
    };

    const palletDropdownOpen = ref(false);
    const palletKeyword = ref("");
    const palletScanVisible = ref(false);
    const palletLoading = ref(false);
    const palletError = ref("");
    const palletOptions = ref([]);
    const selectedNewPallet = ref(null);
    const selectedNewPalletId = computed(() =>
      String(selectedNewPallet.value?.id || ""),
    );
    let palletSearchTimer = null;

    const handlePalletSearch = async () => {
      const keyword = String(palletKeyword.value || "").trim();
      palletError.value = "";
      palletOptions.value = [];
      if (!keyword) return;

      palletLoading.value = true;
      try {
        const res = await getPalletCodeList({ palletCode: keyword });
        const data = res?.data;
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];

        if (list.length === 1) {
          // 如果只有一个托盘记录，直接选择
          confirmNewPallet(list[0]);
        } else {
          // 如果大于1条记录，显示下拉选择
          palletOptions.value = list;
        }
      } catch (e) {
        palletError.value = e?.message || "搜索失败";
      } finally {
        palletLoading.value = false;
      }
    };

    const openPalletDropdown = () => {
      palletDropdownOpen.value = true;
      palletError.value = "";
      palletOptions.value = [];
      if (String(palletKeyword.value || "").trim()) {
        handlePalletSearch();
      }
    };

    const startPalletScan = () => {
      palletScanVisible.value = true;
    };

    const handlePalletScanSuccess = (decodedText) => {
      const text = String(decodedText || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[\r\n\t]/g, "")
        .trim();

      if (!text) return;

      palletKeyword.value = text;
      palletScanVisible.value = false;
      palletDropdownOpen.value = true;
      handlePalletInput();
    };

    const handlePalletInput = async () => {
      selectedNewPallet.value = null;
      palletDropdownOpen.value = true;
      if (palletSearchTimer) clearTimeout(palletSearchTimer);
      palletSearchTimer = setTimeout(() => {
        handlePalletSearch();
      }, 300);
    };

    const confirmNewPallet = (p) => {
      if (!p?.id) return;
      selectedNewPallet.value = p;
      palletKeyword.value = String(p?.palletCode || "");
      palletDropdownOpen.value = false;

      // 如果新托盘是高架仓托盘(palletType === 1)，自动填充库位
      if (Number(p.palletType) === 1 && p.wareId) {
        locationMode.value = "scan";
        scannedWareLocation.value = {
          id: p.wareId,
          wareName: p.wareName,
          wareNo: p.wareNo,
          wareAreaName: p.wareAreaName,
        };
        scanWareNo.value = p.wareName || p.wareNo || "";
        newWareId.value = String(p.wareId);
      }
    };

    const handleWareScan = async () => {
      // 如果是高架仓托盘，不执行搜索（避免覆盖自动填充的值）
      if (isHighRackPallet.value) {
        return;
      }

      const wareNo = String(scanWareNo.value || "").trim();
      if (!wareNo) {
        scanWareError.value = "";
        scanWareResults.value = [];
        scannedWareLocation.value = null;
        newWareId.value = "";
        return;
      }

      scanWareLoading.value = true;
      scanWareError.value = "";
      try {
        const res = await getWarePage({ wareNo, wareType: 2 });
        const data = res?.data || {};
        const records = Array.isArray(data.records) ? data.records : [];

        if (records.length === 0) {
          scanWareError.value = "未找到匹配的库位";
          scannedWareLocation.value = null;
          newWareId.value = "";
        } else if (records.length === 1) {
          scannedWareLocation.value = records[0];
          newWareId.value = String(records[0]?.id || "");
          scanWareError.value = "";
        } else {
          scanWareResults.value = records;
          showWareSelectModal.value = true;
        }
      } catch (e) {
        scanWareError.value = e?.message || "查询失败";
        scannedWareLocation.value = null;
        newWareId.value = "";
      } finally {
        scanWareLoading.value = false;
      }
    };

    const selectScannedWare = (ware) => {
      scannedWareLocation.value = ware;
      newWareId.value = String(ware?.id || "");
      showWareSelectModal.value = false;
    };

    const closeWareSelectModal = () => {
      showWareSelectModal.value = false;
    };

    const normalizeQty = (val) => {
      const s = String(val ?? "").trim();
      if (!s) return null;
      const n = Number(s);
      if (!Number.isFinite(n) || n <= 0) return null;
      return n;
    };

    const submit = async () => {
      if (!selectedRow.value) {
        toast.error("请选择一个批次");
        return;
      }
      if (!asId(newWareId.value)) {
        toast.error("请选择新库位");
        return;
      }
      if (!asId(selectedNewPallet.value?.id)) {
        toast.error("请选择新托盘码（需从下拉结果中选中）");
        return;
      }
      const qty = normalizeQty(moveQty.value);
      if (!qty) {
        toast.error("请输入数量");
        return;
      }

      try {
        await ElMessageBox.confirm("确定要执行确认转换吗？", "确认提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        });
      } catch {
        return;
      }

      await handleConfirmSubmit();
    };

    const handleConfirmSubmit = async () => {
      const qty = normalizeQty(moveQty.value);

      const payload = {
        direction: Number(direction.value),
        fromPalletCode: String(palletCode.value || "").trim(),
        fromPalletId: String(
          selectedRow.value?.palletId || palletDetail.value?.id || "",
        ),
        fromWareId: String(
          selectedRow.value?.wareId || palletDetail.value?.wareId || "",
        ),
        storeId: String(
          selectedRow.value?.storeId || palletDetail.value?.storeId || "",
        ),
        toWareId: String(newWareId.value || ""),
        toPalletId: String(selectedNewPallet.value?.id || ""),
        materialId: String(selectedRow.value?.materialId || ""),
        batchNo: String(selectedRow.value?.batchNo || ""),
        materialNum: qty,
        remark: String(remark.value || "").trim(),
      };

      submitLoading.value = true;
      try {
        await qualityTransferMove(payload);
        toast.success("转换成功");
        closeConfirmModal();
        resetConfirmForm();
        selectedRow.value = null;
        await handleSearch();
      } catch (e) {
        toast.error(e?.message || "提交失败");
      } finally {
        submitLoading.value = false;
      }
    };

    onUnmounted(() => {
      if (palletSearchTimer) clearTimeout(palletSearchTimer);
      if (fromPalletSearchTimer) clearTimeout(fromPalletSearchTimer);
    });

    return {
      handleBack,
      palletCode,
      fromPalletScanVisible,
      startFromPalletScan,
      handleFromPalletScanSuccess,
      clearPalletCode,
      preMoveLoading,
      showConfirmModal,
      closeConfirmModal,
      fromPalletDropdownOpen,
      fromPalletLoading,
      fromPalletError,
      fromPalletOptions,
      openFromPalletDropdown,
      handleFromPalletInput,
      confirmFromPallet,
      newWareText: newWareTextComputed,
      openWareSelector,
      showWareSelector,
      closeWareSelector,
      wareSelectorLoading,
      wareSelectorError,
      warehouseName,
      wareAreas,
      activeAreaId,
      activeAreaName,
      activeLocations,
      selectedLocationId,
      selectArea,
      selectLocation,
      confirmWareSelector,
      palletDropdownOpen,
      palletKeyword,
      palletScanVisible,
      startPalletScan,
      handlePalletScanSuccess,
      clearPalletKeyword,
      palletLoading,
      palletError,
      palletOptions,
      selectedNewPalletId,
      openPalletDropdown,
      handlePalletInput,
      confirmNewPallet,

      // 扫码库位相关
      locationMode,
      scanWareNo,
      scanWareLoading,
      scanWareError,
      scannedWareLocation,
      handleWareScan,
      selectScannedWare,
      closeWareSelectModal,
      showWareSelectModal,
      scanWareResults,

      loading,
      errorMsg,
      searched,
      materialGroups,
      selectedRow,
      selectedRowKey,
      rowKeyOf,
      selectMaterial,
      direction,
      moveQty,
      remark,
      submitLoading,
      submit,
      handleSearch,

      // 物料编码搜索相关
      materialCodeKeyword,
      materialCodeDropdownOpen,
      materialCodeLoading,
      materialCodeError,
      materialCodeOptions,
      openMaterialCodeDropdown,
      closeMaterialCodeDropdown,
      handleMaterialCodeInput,
      confirmMaterialCode,
      clearMaterialCode,
      selectedMaterialId,

      // 供应商搜索相关
      supplierKeyword,
      supplierDropdownOpen,
      supplierLoading,
      supplierError,
      supplierOptions,
      selectedSupplier,
      selectedSupplierName,
      openSupplierDropdown,
      closeSupplierDropdown,
      handleSupplierInput,
      clearSupplier,
      confirmSupplier,
      closeAllDropdowns,

      // 分页相关
      palletDetail,
      currentPage,
      pageSize,
      hasNextPage,
      handlePrevPage,
      handleNextPage,
      formatQty,
      formatBatchNo,
      wareParentId,
      warehouseLoading,
      warehouseList,
      selectedWarehouseId,
      locationSearchKeyword,
      handleLocationSearch,
      selectWarehouse,
      selectAllLocations,
    };
  },
};
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: var(--background-color);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(64px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  border: none;
  background: transparent;
  padding: 6px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-icon {
  width: 22px;
  height: 22px;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
}

.header-right {
  width: 40px;
  height: 40px;
}

.content {
  padding: 12px;
  margin-top: calc(64px + env(safe-area-inset-top));
  padding-bottom: calc(96px + env(safe-area-inset-bottom));
  min-height: calc(
    100vh - 64px - env(safe-area-inset-top) - 96px - env(safe-area-inset-bottom)
  );
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.panel {
  background: var(--card-background);
  border-radius: 14px;
  padding: 12px;
  box-shadow: 0 2px 10px var(--shadow-light);
  border: 1px solid var(--border-color);
  margin-bottom: 12px;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.status {
  padding: 16px 0;
  text-align: center;
  color: var(--text-secondary);
}

.row {
  display: grid;
  grid-template-columns: 78px 1fr 20px;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.row-btn {
  width: 100%;
  border: none;
  background: transparent;
  padding: 14px 12px;
  display: grid;
  grid-template-columns: 78px 1fr 20px;
  align-items: center;
}

.label {
  font-size: 12px;
  color: var(--text-secondary);
  width: 78px;
  flex-shrink: 0;
}

.required {
  color: #ef4444;
  margin-left: 2px;
}

.value {
  font-size: 13px;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.value.placeholder {
  color: var(--text-secondary);
}

.arrow {
  font-size: 14px;
  color: var(--text-secondary);
  text-align: right;
  width: 20px;
  flex-shrink: 0;
}

.primary-btn {
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 12px;
  background: var(--primary-color);
  color: var(--primary-text);
  font-size: 15px;
  font-weight: 700;
}

.primary-btn:disabled {
  opacity: 0.6;
}

.material-groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.material-group {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
}

.material-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(35, 177, 192, 0.12);
  border-bottom: 1px solid var(--border-color);
}

.batch-list {
  display: flex;
  flex-direction: column;
}

.batch-item {
  width: 100%;
  border: none;
  background: var(--card-background);
  padding: 10px 12px;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.batch-item + .batch-item {
  border-top: 1px solid var(--border-color);
}

.batch-item.selected {
  background: rgba(35, 177, 192, 0.12);
}

.batch-main {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.batch-sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
}

.badge-good {
  background: rgba(16, 185, 129, 0.12);
  color: var(--success-color);
  border-color: rgba(16, 185, 129, 0.3);
}

.badge-bad {
  background: rgba(239, 68, 68, 0.12);
  color: var(--error-color);
  border-color: rgba(239, 68, 68, 0.3);
}

.material-item {
  width: 100%;
  border: 1px solid var(--border-color);
  background: var(--card-background);
  border-radius: 12px;
  padding: 10px 12px;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.material-item.selected {
  border-color: var(--primary-color);
  background: rgba(35, 177, 192, 0.12);
}

.material-left {
  flex: 1 1 auto;
  min-width: 0;
}

.material-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
}

.material-title-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.supplier {
  max-width: 140px;
  flex: 0 1 auto;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.material-no {
  flex: 1 1 0;
  min-width: 0;
  font-size: 13px;
  font-weight: 800;
  color: var(--text-primary);
  white-space: normal;
  word-break: break-all;
}

.material-sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-wrap {
  position: relative;
  width: 100%;
}

.dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: 38px;
  z-index: 10;
  filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.12));
}

.pallet-options {
  margin-top: 6px;
  max-height: 260px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--card-background);
}

.pallet-option {
  width: 100%;
  text-align: left;
  border: none;
  background: var(--card-background);
  padding: 10px 12px;
}

.pallet-option + .pallet-option {
  border-top: 1px solid var(--border-color);
}

.pallet-option.active {
  background: rgba(35, 177, 192, 0.12);
}

.pallet-option-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.pallet-option-main {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
}

.pallet-option-sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

.selector {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.modal-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--card-background);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.modal-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-close {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.modal-body {
  padding: 16px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
}

.modal-summary {
  padding: 10px 12px;
  background: rgba(35, 177, 192, 0.12);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-bottom: 12px;
}

.sum-main {
  font-size: 13px;
  font-weight: 800;
  color: var(--text-primary);
}

.sum-sub {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-foot {
  padding: 12px;
  border-top: 1px solid var(--border-color);
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
}

.selector-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.selector-panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 85vh;
  max-height: 600px;
  background: var(--card-background);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.selector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-color);
  background: var(--primary-color);
  flex-shrink: 0;
}

.selector-back {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 36px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.selector-title {
  font-size: 15px;
  font-weight: 600;
  color: white;
}

.selector-right {
  width: 36px;
  height: 36px;
}

.selector-search {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  background: var(--primary-color);
}

.search-input {
  width: 100%;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 18px;
  padding: 0 16px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  box-sizing: border-box;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.selector-warehouse-bar {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-color);
}

.selector-warehouse-loading {
  font-size: 13px;
  color: var(--text-secondary);
}

.warehouse-tabs {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.warehouse-tab {
  flex: 0 0 auto;
  border: 1px solid var(--border-color) !important;
  background: var(--card-background) !important;
  color: var(--text-primary) !important;
  padding: 8px 16px !important;
  border-radius: 999px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
}
.warehouse-tab.active {
  border-color: var(--primary-color) !important;
  background: var(--primary-color) !important;
  color: white !important;
  font-weight: 700 !important;
}

.selector-loading,
.selector-error {
  padding: 16px 12px;
  text-align: center;
  color: var(--text-secondary);
}

.selector-body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.areas {
  width: 120px;
  background: var(--card-background);
  padding: 8px;
  overflow: auto;
}

.area-item {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 12px 10px;
  border-radius: 10px;
  margin-bottom: 8px;
  color: var(--text-primary);
  font-size: 14px;
}

.area-item.active {
  background: var(--card-background);
  font-weight: 700;
  box-shadow: 0 1px 0 var(--shadow-light);
}

.locations {
  flex: 1;
  padding: 12px;
  overflow: auto;
}

.locations-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.location-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.location-card {
  border: 1px solid var(--border-color);
  background: var(--card-background);
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
  transition: all 0.2s ease;
}

.location-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.location-card.selected {
  border-color: var(--primary-color);
  background: rgba(35, 177, 192, 0.1);
  box-shadow: 0 0 0 3px rgba(35, 177, 192, 0.2);
}

.location-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.location-sub {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.selector-footer {
  padding: 10px 12px 16px;
  border-top: 1px solid var(--border-color);
  background: var(--card-background);
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
}

.selector-confirm {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: var(--primary-color);
  color: var(--primary-text);
  font-size: 16px;
  font-weight: 700;
}

.selector-confirm:disabled {
  background: var(--border-color);
  color: var(--text-secondary);
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .content {
    padding: 10px;
    padding-bottom: calc(96px + env(safe-area-inset-bottom));
  }

  .row {
    grid-template-columns: 64px 1fr;
    gap: 8px;
  }

  .panel {
    border-radius: 12px;
    padding: 10px;
  }

  .panel-title {
    margin-bottom: 8px;
  }

  .material-group-head {
    padding: 10px;
  }

  .batch-item {
    padding: 10px;
  }

  .supplier {
    max-width: 112px;
    font-size: 11px;
  }

  .badge {
    font-size: 10px;
    padding: 2px 6px;
  }

  .material-right {
    gap: 8px;
  }

  .qty .v {
    font-size: 12px;
  }

  .selector-body {
    min-height: 0;
  }
}

.qty {
  text-align: right;
}

.qty .k {
  font-size: 11px;
  color: var(--text-secondary);
}

.qty .v {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
}

.material-check {
  width: 18px;
  height: 18px;
}

.seg {
  display: flex;
  gap: 8px;
}

.seg-btn {
  flex: 1 1 0;
  border: 1px solid var(--border-color);
  background: var(--card-background);
  border-radius: 10px;
  padding: 10px;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
}

.seg-btn.active {
  border-color: var(--primary-color);
  background: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.input {
  width: 100%;
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  padding: 0 76px 0 12px;
  font-size: 13px;
  box-sizing: border-box;
  background-color: var(--card-background);
  color: var(--text-primary);
}

.input-with-scan {
  position: relative;
}

.input-with-scan .input-clear {
  right: 36px;
}

.input-with-scan .scan-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  cursor: pointer;
  color: var(--primary-color);
  padding: 0;
}

.input-clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  color: #6b7280;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-clear:hover {
  background: rgba(0, 0, 0, 0.2);
}

.action-btn {
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 12px;
  background: var(--primary-color);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  margin-top: 8px;
}

.action-btn:disabled {
  opacity: 0.6;
}

.form-tip {
  margin-top: 8px;
  font-size: 12px;
  color: var(--text-secondary);
}

/* 分页控件样式 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px;
  background: var(--card-background);
  margin: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--background-color);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: var(--primary-text);
  border-color: var(--primary-color);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* 模式切换样式 */
.mode-switch {
  display: flex;
  gap: 8px;
  padding: 0 16px 12px;
  border-bottom: 1px solid var(--border-color);
}

.mode-btn {
  flex: 1;
  height: 40px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-background);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-btn.active {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: white;
  font-weight: 600;
}

/* 下拉加载样式 */
.dropdown-loading {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: var(--text-secondary);
}

/* 表单错误提示 */
.form-error {
  padding: 8px 16px;
  color: #ef4444;
  font-size: 13px;
}

/* 扫码结果显示样式 */
.scanned-ware-info {
  margin: 0 16px;
  padding: 10px 16px;
  background: rgba(35, 177, 192, 0.12);
  border-radius: 8px;
  margin-top: 8px;
}

.scanned-ware-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.scanned-ware-area {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
}

/* 库位列表样式 */
.ware-list {
  padding: 8px 0;
}

.ware-item {
  width: 100%;
  border: none;
  background: transparent;
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
}

.ware-item:last-child {
  border-bottom: none;
}

.ware-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.ware-area {
  margin-top: 4px;
  font-size: 13px;
  color: var(--text-secondary);
}

</style>
